import * as mobx from 'mobx';
import { AccountAdminSettingsType, ICommonSettingsDTO } from '../../../api/account-admin';
import { IZoomGroupDTO } from '../../../api/zoom.dao';
import { EnablementStatus } from '../../../constants/common.constants';
import { IEventBlueprintDTO } from '../../../api/blueprints/event.blueprint.types';

interface IAccountAdminSettingsDTO {
    accountId: number;
    enabled: boolean;
    type: AccountAdminSettingsType;
    additionalData?: number | IZoomGroupDTO[] | string[] | IEventBlueprintDTO | string;
    coordinatorDeniedNotificationsEnabled?: boolean;
    requestAccessCodeRegistrationApprovalEnabled?: boolean;
}

/**
 * Represents an account admin setting entity with properties
 * for managing account settings. Provides methods to manipulate and
 * retrieve account settings data, including enabling/disabling settings,
 * updating additional data, and resetting to a snapshot state.
 */
class AccountAdminSettingEntity {
    @mobx.observable
    private accountIdValue: number;
    @mobx.observable
    private isEnabled: boolean = false;
    @mobx.observable
    private typeValue: AccountAdminSettingsType;
    @mobx.observable
    private additionalDataValue: number | IZoomGroupDTO[] | string[] | IEventBlueprintDTO | string;
    @mobx.observable
    private isCoordinatorDeniedNotificationsEnabled: boolean = false;
    @mobx.observable
    private isRequestAccessCodeRegistrationApprovalEnabled: boolean = false;
    @mobx.observable.deep
    private snapshot: IAccountAdminSettingsDTO;

    constructor(dto: IAccountAdminSettingsDTO) {
        this.fromDTO(dto);
        this.snapshot = { ...dto };
    }

    public get enabled(): boolean {
        return this.isEnabled;
    }

    public get type(): AccountAdminSettingsType {
        return this.typeValue;
    }

    public get additionalData(): number | IZoomGroupDTO[] | string[] | IEventBlueprintDTO | string {
        return this.additionalDataValue;
    }

    public get coordinatorDeniedNotificationsEnabled(): boolean {
        return this.isCoordinatorDeniedNotificationsEnabled;
    }

    public get requestAccessCodeRegistrationApprovalEnabled(): boolean {
        return this.isRequestAccessCodeRegistrationApprovalEnabled;
    }

    public get accountId(): number {
        return this.accountIdValue;
    }

    public get suffix(): EnablementStatus {
        return this.enabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
    }

    public get snapshotSuffix(): EnablementStatus {
        const isEnabled = this.snapshot.enabled
            || Boolean(this.snapshot.requestAccessCodeRegistrationApprovalEnabled);
        return isEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
    }

    @mobx.action
    public setEnabled = (enabled: boolean): void => {
        this.isEnabled = enabled;
    };

    @mobx.action
    public toggleEnabled = (): void => {
        this.isEnabled = !this.isEnabled;
    };

    @mobx.action
    public setAdditionalData = (additionalData: number | IZoomGroupDTO[] | string[] | IEventBlueprintDTO | string): void => {
        this.additionalDataValue = additionalData;
    };

    @mobx.action
    public setCoordinatorDeniedNotificationsEnabled = (enabled: boolean): void => {
        this.isCoordinatorDeniedNotificationsEnabled = enabled;
    };

    @mobx.action
    public setRequestAccessCodeRegistrationApprovalEnabled = (enabled: boolean): void => {
        this.isRequestAccessCodeRegistrationApprovalEnabled = enabled;
    };

    public getCommonAPIPayload = (): ICommonSettingsDTO => {
        return {
            accountId: this.accountId,
            type: this.type,
        };
    };

    @mobx.action
    public reset = (): void => {
        this.fromDTO(this.snapshot);
    };

    @mobx.action
    public updateSnapshot = (): void => {
        this.snapshot.accountId = this.accountId;
        this.snapshot.enabled = this.enabled;
        this.snapshot.type = this.type;
        this.snapshot.additionalData = this.additionalData;
        this.snapshot.coordinatorDeniedNotificationsEnabled = this.coordinatorDeniedNotificationsEnabled;
        this.snapshot.requestAccessCodeRegistrationApprovalEnabled =
            this.requestAccessCodeRegistrationApprovalEnabled;
    };

    private fromDTO = (dto: IAccountAdminSettingsDTO): void => {
        this.accountIdValue = dto.accountId;
        this.isEnabled = dto.enabled;
        this.typeValue = dto.type;
        this.additionalDataValue = dto.additionalData;
        this.isCoordinatorDeniedNotificationsEnabled = Boolean(dto.coordinatorDeniedNotificationsEnabled);
        this.isRequestAccessCodeRegistrationApprovalEnabled =
            Boolean(dto.requestAccessCodeRegistrationApprovalEnabled);
    };
}

export { AccountAdminSettingEntity, IAccountAdminSettingsDTO };